import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Trophy, Mail, PhoneCall, Pencil, Ban, Trash2 } from "lucide-react";
import ConfirmAction from "@/components/ConfirmAction";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { uomLabel } from "@/constants/uom";
import DocumentsPanel from "@/components/DocumentsPanel";
import { fmtDate, fmtDateTime } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";
import JobDeliveryAddress from "@/components/JobDeliveryAddress";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function RFQDetail() {
  const { id } = useParams();
  const [rfq, setRfq] = useState(null);
  const [costCodes, setCostCodes] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [addressHint, setAddressHint] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [manualVendorId, setManualVendorId] = useState("");
  const [manualItems, setManualItems] = useState([]);
  const [manualLeadTime, setManualLeadTime] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  const [showAward, setShowAward] = useState(false);
  const [awardQuoteId, setAwardQuoteId] = useState(null);
  const [awardAddress, setAwardAddress] = useState("");
  const [addressFixed, setAddressFixed] = useState(false);
  const [awardExpectedDate, setAwardExpectedDate] = useState("");
  const [awardVendorNotes, setAwardVendorNotes] = useState("");
  const navigate = useNavigate();
  const [rfqAction, setRfqAction] = useState(null); // cancel | delete
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDue, setEditDue] = useState("");

  const load = () => api.get(`/contractor/rfqs/${id}`).then((r) => setRfq(r.data));
  useEffect(() => {
    load();
    api.get("/contractor/cost-codes", { params: { include_inactive: true } }).then((r) => setCostCodes(r.data));
    api.get("/contractor/vendor-network").then((r) => setVendors(r.data));
  }, [id]);

  // Package 23 — award's delivery address is filtered to this RFQ's job
  // profile, same rule as everywhere else a job context picks an address.
  useEffect(() => {
    if (!rfq) return;
    // Job site or main office only (Test Drive 2026-10-21).
    api.get("/contractor/po-delivery-addresses", { params: { job_id: rfq.job_id || undefined } }).then((r) => {
      setAddresses(r.data.addresses);
      setAddressHint(r.data.hint);
      setAddressFixed(!!r.data.fixed);
      if (r.data.fixed) setAwardAddress(r.data.preselect_id);
      else if (!awardAddress && r.data.preselect_id) setAwardAddress(r.data.preselect_id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfq?.job_id]);

  if (!rfq) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const costCodeLabel = (ccId) => {
    const cc = costCodes.find((c) => c.id === ccId);
    return cc ? `${cc.code} — ${cc.name}` : "—";
  };
  const invitedVendors = vendors.filter((v) => rfq.vendor_ids.includes(v.vendor_id));

  const openManualQuote = () => {
    setManualVendorId("");
    setManualItems(rfq.items.map((i) => ({ description: i.description, qty: i.qty, unit_price: 0 })));
    setManualLeadTime("");
    setManualNotes("");
    setShowManual(true);
  };

  const updateManualPrice = (i, value) => {
    const next = [...manualItems];
    next[i] = { ...next[i], unit_price: Number(value) };
    setManualItems(next);
  };
  const manualTotal = manualItems.reduce((s, i) => s + i.qty * i.unit_price, 0);

  const submitManualQuote = async () => {
    if (!manualVendorId) return toast.error("Select which vendor this quote is for");
    try {
      await api.post(`/contractor/rfqs/${id}/manual-quote`, {
        vendor_id: manualVendorId, items: manualItems,
        lead_time_days: manualLeadTime ? Number(manualLeadTime) : null, notes: manualNotes || null,
      });
      toast.success("Quote entered");
      setShowManual(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to enter quote");
    }
  };

  const openAward = (quoteId) => {
    setAwardQuoteId(quoteId);
    setAwardAddress(""); setAwardExpectedDate(""); setAwardVendorNotes("");
    setShowAward(true);
  };

  const award = async () => {
    if (!awardAddress) return toast.error("Select a delivery address");
    try {
      const { data } = await api.post(`/contractor/rfqs/${id}/award`, {
        quote_id: awardQuoteId, delivery_address_id: awardAddress,
        expected_delivery_date: awardExpectedDate || null, vendor_notes: awardVendorNotes || null,
      });
      toast.success("Vendor awarded — purchase request submitted for approval");
      navigate(`/contractor/purchase-requests/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message).join(" ") : detail || "Failed to award");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="rfq-detail-page">
      <Link to="/contractor/purchase-orders" className="text-[12.5px] text-slate-500 flex items-center gap-1 mb-3"><ChevronLeft size={13} /> Back to Purchase Orders</Link>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-extrabold tracking-tight mono">{rfq.rfq_number}</h1>
          <StatusBadge status={rfq.status} />
        </div>
        {(rfq.status === "open" || rfq.status === "cancelled") && (
          <div className="flex gap-2">
            {rfq.status === "open" && (
              <>
                <button onClick={() => { setEditTitle(rfq.title); setEditDue(rfq.due_date || ""); setEditOpen(true); }} data-testid="edit-rfq-button" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50"><Pencil size={13} /> Edit</button>
                <button onClick={() => setRfqAction("cancel")} data-testid="cancel-rfq-button" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 text-[12.5px] font-semibold text-red-600 hover:bg-red-50"><Ban size={13} /> Cancel</button>
              </>
            )}
            <button onClick={() => setRfqAction("delete")} data-testid="delete-rfq-button" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 text-[12.5px] font-semibold text-red-600 hover:bg-red-50"><Trash2 size={13} /> Delete</button>
          </div>
        )}
      </div>
      {rfq.status === "cancelled" && rfq.cancelled_reason && <p className="text-[12.5px] text-red-700 mt-1">Cancelled — {rfq.cancelled_reason}</p>}

      <ConfirmAction open={!!rfqAction} onOpenChange={(o) => !o && setRfqAction(null)}
        title={rfqAction === "cancel" ? `Cancel ${rfq.rfq_number}?` : `Delete ${rfq.rfq_number}?`}
        description={rfqAction === "cancel" ? "Vendors are told it's cancelled and can't quote any more. Quotes already in stay on record." : "Only possible while nobody has quoted. It's removed completely."}
        confirmLabel={rfqAction === "cancel" ? "Cancel RFQ" : "Delete"} requireReason={rfqAction === "cancel"}
        reasonPlaceholder="Why is it cancelled? (vendors see this)" testId="rfq-action-dialog"
        onConfirm={async (reason) => {
          if (rfqAction === "cancel") {
            await api.post(`/contractor/rfqs/${rfq.id}/cancel`, { reason });
            toast.success("RFQ cancelled — vendors notified");
            load();
          } else {
            await api.delete(`/contractor/rfqs/${rfq.id}`);
            toast.success("RFQ deleted");
            navigate("/contractor/purchase-orders");
          }
        }} />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-rfq-dialog">
          <DialogHeader><DialogTitle>Edit {rfq.rfq_number}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Title" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <div>
              <label className="text-[12.5px] text-slate-500">Quotes due</label>
              <input type="date" value={editDue} onChange={(e) => setEditDue(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <button onClick={async () => {
              try {
                await api.patch(`/contractor/rfqs/${rfq.id}`, { title: editTitle, due_date: editDue || "" });
                toast.success("RFQ updated");
                setEditOpen(false);
                load();
              } catch (e) { toast.error(e.response?.data?.detail?.[0]?.message || e.response?.data?.detail || "Failed to update"); }
            }} data-testid="save-rfq-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Save</button>
          </div>
        </DialogContent>
      </Dialog>
      <p className="text-slate-500 text-sm mt-1">{rfq.title} · Due {rfq.due_date ? fmtDate(rfq.due_date) : "—"}</p>

      {rfq.status === "awarded" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[12.5px]" data-testid="rfq-awarded-links">
          <span className="text-slate-500">Awarded —</span>
          {rfq.awarded_pr_id && <Link to={`/contractor/purchase-requests/${rfq.awarded_pr_id}`} className="text-sky-600 font-semibold" data-testid="rfq-linked-pr">View Purchase Request</Link>}
          {rfq.awarded_po_id && <><span className="text-slate-300">·</span><Link to={`/contractor/purchase-orders/${rfq.awarded_po_id}`} className="text-sky-600 font-semibold" data-testid="rfq-linked-po">View Purchase Order</Link></>}
        </div>
      )}

      <div className="mt-7">
        <div className="text-[14.5px] font-bold mb-3">Requested Items</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                <th className="text-left py-2">Description</th><th className="text-left py-2">Cost Code</th><th className="text-right py-2">Qty</th>
              </tr>
            </thead>
            <tbody>
              {rfq.items.map((it, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-2">{it.description}</td>
                  <td className="py-2 text-slate-500">{costCodeLabel(it.cost_code_id)}</td>
                  <td className="py-2 text-right">{it.qty} {uomLabel(it.uom || it.unit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8">
        <div className="text-[14.5px] font-bold mb-3">Distribution</div>
        <div className="space-y-1.5" data-testid="rfq-distribution-list">
          {(rfq.distribution || []).map((d, i) => (
            <div key={i} className="flex items-center gap-2 text-[12.5px] text-slate-600" data-testid={`distribution-row-${i}`}>
              <Mail size={13} className="text-slate-400" />
              <span className="font-medium">{d.vendor_name}</span>
              {d.email ? <span className="text-slate-400">{d.email} · emailed {fmtDateTime(d.sent_at)}</span> : <span className="text-amber-600">no contact email on file</span>}
            </div>
          ))}
          {(!rfq.distribution || rfq.distribution.length === 0) && <div className="text-slate-400 text-[12.5px]">No distribution record.</div>}
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[14.5px] font-bold">Quote Comparison ({rfq.quotes.length} submitted)</div>
          {rfq.status === "open" && (
            <button onClick={openManualQuote} data-testid="enter-manual-quote-button" className="flex items-center gap-1.5 text-[12.5px] font-semibold text-sky-600">
              <PhoneCall size={13} /> Enter Quote Manually
            </button>
          )}
        </div>
        {rfq.quotes.length === 0 ? (
          <div className="text-[13px] text-slate-400">No quotes submitted yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="quote-comparison">
            {rfq.quotes.map((q) => (
              <div key={q.id} className={`border rounded-xl p-4 ${q.status === "awarded" ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`} data-testid={`quote-card-${q.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-bold text-[14px]">{q.vendor_name}</div>
                  {q.status === "awarded" && <Trophy size={16} className="text-emerald-600" />}
                </div>
                {q.source === "manual" && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid={`manually-entered-badge-${q.id}`}>
                    <PhoneCall size={10} /> Manually entered{q.entered_by ? ` by ${q.entered_by}` : ""}
                  </div>
                )}
                <div className="text-[26px] font-extrabold mono mt-2">{fmt(q.total)}</div>
                <div className="text-[12px] text-slate-500 mt-1">Lead time: {q.lead_time_days ?? "—"} days</div>
                {q.notes && <div className="text-[12px] text-slate-500 mt-1">"{q.notes}"</div>}
                <div className="mt-3 space-y-1">
                  {q.items.map((it, i) => (
                    <div key={i} className="flex justify-between text-[12px] text-slate-600">
                      <span>{it.description} × {it.qty}</span><span className="mono">{fmt(it.qty * it.unit_price)}</span>
                    </div>
                  ))}
                </div>
                {rfq.status === "open" && (
                  <button onClick={() => openAward(q.id)} data-testid={`award-quote-${q.id}`} className="mt-4 w-full h-9 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">Award to {q.vendor_name}</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <DocumentsPanel entityType="rfq" entityId={rfq.id} />

      <Dialog open={showManual} onOpenChange={setShowManual}>
        <DialogContent data-testid="manual-quote-modal">
          <DialogHeader><DialogTitle>Enter Quote Manually</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-[12.5px] text-slate-500">For a quote received by phone or email — key it in on the vendor's behalf.</p>
            <div>
              <label className="text-[13px] font-medium">Vendor *</label>
              <SearchableSelect value={manualVendorId} onChange={(e) => setManualVendorId(e.target.value)} data-testid="manual-quote-vendor-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                <option value="">Select vendor…</option>
                {invitedVendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
              </SearchableSelect>
            </div>
            <div className="space-y-2">
              {manualItems.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-6 text-[13px]">{it.description} <span className="text-slate-400">× {it.qty}</span></div>
                  <input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => updateManualPrice(i, e.target.value)} data-testid={`manual-quote-price-${i}`} className="col-span-3 h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]" placeholder="Unit price" />
                  <div className="col-span-3 text-right mono text-[13px] font-medium">{fmt(it.qty * it.unit_price)}</div>
                </div>
              ))}
            </div>
            <div className="text-right text-[14px] font-bold">Total: {fmt(manualTotal)}</div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" min="0" value={manualLeadTime} onChange={(e) => setManualLeadTime(e.target.value)} placeholder="Lead time (days)" data-testid="manual-quote-leadtime-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input value={manualNotes} onChange={(e) => setManualNotes(e.target.value)} placeholder="Notes" data-testid="manual-quote-notes-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <button onClick={submitManualQuote} data-testid="confirm-manual-quote-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Save Quote</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAward} onOpenChange={setShowAward}>
        <DialogContent data-testid="award-modal">
          <DialogHeader><DialogTitle>Award Quote</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-[12.5px] text-slate-500">This creates a purchase request from the awarded quote and routes it for approval — no PO is created directly.</p>
            <div>
              <label className="text-[13px] font-medium">Delivery Address *</label>
              {addressFixed ? (
                <JobDeliveryAddress address={addresses[0]} jobId={rfq.job_id} testId="award-job-delivery-address" />
              ) : (
              <SearchableSelect value={awardAddress} onChange={(e) => setAwardAddress(e.target.value)} data-testid="award-delivery-address-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                <option value="">Select address…</option>
                {addresses.map((a) => <option key={a.id} value={a.id}>{a.label}{a.is_default ? " (default)" : ""}</option>)}
              </SearchableSelect>
              )}
              {addressHint && <p className="text-[11.5px] text-slate-400 mt-1" data-testid="award-address-fallback-hint">{addressHint}</p>}
            </div>
            <div>
              <label className="text-[13px] font-medium">Expected Delivery</label>
              <input type="date" value={awardExpectedDate} onChange={(e) => setAwardExpectedDate(e.target.value)} data-testid="award-expected-date-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-[13px] font-medium">Vendor Notes</label>
              <textarea value={awardVendorNotes} onChange={(e) => setAwardVendorNotes(e.target.value)} rows={2} data-testid="award-vendor-notes-input" className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
            <button onClick={award} data-testid="confirm-award-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold mt-2">Award &amp; Create Purchase Request</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
